import { Box, Text, VStack, HStack, Badge, Divider, FormControl, FormLabel, Select } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { AgentOption } from '@/types/ticket'
import type { FieldEngineerSummary } from '@/store/parentTicket'

type Option = { label: string; value: string }

type Props = {
  /** Actual agent data from API */
  agent?: AgentOption | null
  /** Actual primary engineer data from API */
  primaryEngineer?: FieldEngineerSummary | null
  /** Actual backup engineer data from API */
  backupEngineer?: FieldEngineerSummary | null

  /** Legacy props for editable mode (ParentTicketDetail page) */
  groupValue?: string
  agentValue?: string
  groupOptions?: Option[]
  agentOptions?: Option[]
  onChangeGroup?: (value: string) => void
  onChangeAgent?: (value: string) => void
  isReadOnly?: boolean
}

function PersonCard({
  role,
  name,
  email,
  username,
  employeeId,
  cluster,
  address,
  certifications,
  colorScheme = 'blue',
}: {
  role: string
  name?: string | null
  email?: string | null
  username?: string | null
  employeeId?: string | null
  cluster?: string | null
  address?: string | null
  certifications?: string[]
  colorScheme?: string
}) {
  const colors = useThemeColors()

  return (
    <Box>
      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={1}>
        {role}
      </Text>
      <Box
        p={3}
        borderRadius="md"
        border="1px solid"
        borderColor={colors.borderLight}
        bg={colors.bg}
      >
        <Text fontSize="sm" fontWeight="600" color={colors.text}>
          {name || '—'}
        </Text>
        {username && (
          <Text fontSize="xs" color={colors.textMuted} mt={0.5}>
            @{username}
          </Text>
        )}
        {email && (
          <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>
            {email}
          </Text>
        )}
        {employeeId && (
          <Text fontSize="xs" color={colors.textMuted} mt={0.5}>
            ID: {employeeId}
          </Text>
        )}
        {cluster && (
          <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>
            {cluster}{address ? ` · ${address}` : ''}
          </Text>
        )}
        {certifications && certifications.length > 0 && (
          <HStack spacing={1} mt={1.5} flexWrap="wrap">
            {certifications.map((cert) => (
              <Badge key={cert} fontSize="2xs" colorScheme={colorScheme} variant="subtle" borderRadius="md" px={1.5}>
                {cert}
              </Badge>
            ))}
          </HStack>
        )}
      </Box>
    </Box>
  )
}

export default function Assignment({
  agent,
  primaryEngineer,
  backupEngineer,
  groupValue,
  agentValue,
  groupOptions,
  agentOptions,
  onChangeGroup,
  onChangeAgent,
  isReadOnly = false,
}: Props) {
  const colors = useThemeColors()

  // Rich read-only view when actual data is provided
  const hasRealData = agent || primaryEngineer || backupEngineer
  if (hasRealData) {
    const agentName = agent
      ? [agent.first_name, agent.last_name].filter(Boolean).join(' ') || agent.user_login || '—'
      : null

    return (
      <Box
        bg={colors.cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={colors.border}
        overflow="hidden"
      >
        <Box px={4} py={3} borderBottom="1px solid" borderColor={colors.borderLight}>
          <Text fontWeight="700" color={colors.primary}>
            Assigned Team
          </Text>
        </Box>

        <Box px={4} py={4}>
          <VStack spacing={3} align="stretch">
            {agent && (
              <PersonCard
                role="Helpdesk Agent"
                name={agentName}
                username={agent.user_login}
                email={agent.user_email}
                employeeId={agent.employee_id}
                colorScheme="purple"
              />
            )}

            {primaryEngineer && (
              <>
                {agent && <Divider borderColor={colors.borderLight} />}
                <PersonCard
                  role="Primary Engineer"
                  name={primaryEngineer.full_name}
                  email={primaryEngineer.user_email}
                  cluster={primaryEngineer.cluster}
                  address={primaryEngineer.launch_point}
                  certifications={primaryEngineer.certifications}
                  colorScheme="green"
                />
              </>
            )}

            {backupEngineer && (
              <>
                <Divider borderColor={colors.borderLight} />
                <PersonCard
                  role="Backup Engineer"
                  name={backupEngineer.full_name}
                  email={backupEngineer.user_email}
                  cluster={backupEngineer.cluster}
                  address={backupEngineer.launch_point}
                  certifications={backupEngineer.certifications}
                  colorScheme="orange"
                />
              </>
            )}

            {!agent && !primaryEngineer && !backupEngineer && (
              <Text fontSize="sm" color={colors.textSecondary}>No assignments yet.</Text>
            )}
          </VStack>
        </Box>
      </Box>
    )
  }

  // Fallback: legacy editable dropdowns (ParentTicketDetail page)
  return (
    <Box
      bg={colors.cardBg}
      borderRadius="lg"
      border="1px solid"
      borderColor={colors.border}
      overflow="hidden"
    >
      <Box px={4} py={3} borderBottom="1px solid" borderColor={colors.borderLight}>
        <Text fontWeight="700" color={colors.primary}>
          Assigned Team
        </Text>
      </Box>

      <Box px={4} py={4}>
        <VStack spacing={5} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" color={colors.textSecondary} fontWeight="700" mb={2}>
              Group:
            </FormLabel>
            <Select
              value={groupValue}
              onChange={(e) => onChangeGroup?.(e.target.value)}
              variant="outline"
              bg={colors.inputBg}
              borderColor={colors.inputBorder}
              _hover={{ borderColor: colors.inputborderHover }}
              _focusVisible={{ borderColor: colors.brandBlue, boxShadow: 'none' }}
              isDisabled={isReadOnly}
            >
              {(groupOptions ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" color={colors.textSecondary} fontWeight="700" mb={2}>
              Agent:
            </FormLabel>
            <Select
              value={agentValue}
              onChange={(e) => onChangeAgent?.(e.target.value)}
              variant="outline"
              bg={colors.inputBg}
              borderColor={colors.inputBorder}
              _hover={{ borderColor: colors.inputborderHover }}
              _focusVisible={{ borderColor: colors.brandBlue, boxShadow: 'none' }}
              isDisabled={isReadOnly}
            >
              {(agentOptions ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </VStack>
      </Box>
    </Box>
  )
}
